// Choosing and re-applying the active role (FR-026 to FR-033; R-2, R-4, R-5, R-8; data-model §4, §5).
// Display decisions elsewhere go through 001's effectiveRole, never activeRole directly.
import type { Platform, ServedRole, SignedInSession } from "./types";

export type RoleChoice =
  | { kind: "refusal" }
  | { kind: "role"; activeRole: ServedRole; needsRolePick: boolean };

export function chooseActiveRole(input: {
  served: readonly ServedRole[];
  platform: Platform;
  trigger: "signIn" | "restore";
  hasPendingJoin: boolean;
  remembered: ServedRole | null;
}): RoleChoice {
  const { served, platform, trigger, hasPendingJoin, remembered } = input;
  const parent = served.includes("PARENT");
  const schoolAdmin = served.includes("SCHOOL_ADMIN");
  if (!parent && !schoolAdmin) return { kind: "refusal" };
  if (!schoolAdmin)
    return { kind: "role", activeRole: "PARENT", needsRolePick: false };
  // SCHOOL_ADMIN alone: on Android effectiveRole is null and the unsupported screen explains (FR-028).
  if (!parent)
    return { kind: "role", activeRole: "SCHOOL_ADMIN", needsRolePick: false };
  // Both roles.
  if (platform === "android")
    return { kind: "role", activeRole: "PARENT", needsRolePick: false };
  if (trigger === "restore") {
    return {
      kind: "role",
      activeRole:
        remembered && served.includes(remembered) ? remembered : "PARENT",
      needsRolePick: false,
    };
  }
  return { kind: "role", activeRole: "PARENT", needsRolePick: !hasPendingJoin };
}

export type RolesApplied =
  | { kind: "noAccess" }
  | {
      kind: "keep";
      activeRole: ServedRole;
      notice: string | null;
      clearPreference: boolean;
    };

export const ROLE_NOTICES = {
  upToDate: "Your access is up to date",
  updated: "Access updated",
  schoolAdminRemoved: "Your school admin access has been removed.",
  parentRemoved: "Your parent access has been removed.",
} as const;

/** Re-applies the served roles after a renewal (data-model §5). */
export function applyRoles(
  prev: SignedInSession,
  next: readonly ServedRole[],
  manual: boolean,
): RolesApplied {
  if (next.length === 0) return { kind: "noAccess" };
  if (!next.includes(prev.activeRole)) {
    const other = next[0] as ServedRole;
    return {
      kind: "keep",
      activeRole: other,
      notice:
        prev.activeRole === "SCHOOL_ADMIN"
          ? ROLE_NOTICES.schoolAdminRemoved
          : ROLE_NOTICES.parentRemoved,
      clearPreference: true,
    };
  }
  const before = new Set(prev.roles);
  const gained = next.some((role) => !before.has(role));
  return {
    kind: "keep",
    activeRole: prev.activeRole,
    notice: manual
      ? gained
        ? ROLE_NOTICES.updated
        : ROLE_NOTICES.upToDate
      : null,
    clearPreference: false,
  };
}
